import React, { useEffect, useState } from "react";
import { CheckCircle2, CreditCard, ExternalLink, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import api from "~/services/api";

export default function GlobalAutopay({ session, refresh }) {
  const [authorized, setAuthorized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [diagnostic, setDiagnostic] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(session.autopay_payment_url || "");
  const [changingCard, setChangingCard] = useState(false);

  useEffect(() => {
    setPaymentUrl(session.autopay_disabled ? "" : session.autopay_payment_url || "");
  }, [session.autopay_payment_url, session.autopay_disabled]);

  async function disable() {
    setBusy(true);
    setError("");
    try {
      await api.delete(`/contractsessions/${session.id}/autopay`);
      setPaymentUrl("");
      setAuthorized(false);
      setChangingCard(false);
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.error || "Unable to disable autopay.");
    } finally {
      setBusy(false);
    }
  }

  async function configure() {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/contractsessions/${session.id}/autopay`, {
        student_authorized: authorized,
        replace_card: changingCard,
      });
      setPaymentUrl(data.payment_url || "");
      setChangingCard(false);
      setAuthorized(false);
      if (!data.is_autopay && !data.payment_url) {
        setError("Verification is pending. Check the payment history before starting another payment.");
      }
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.error || "Unable to configure autopay.");
    } finally {
      setBusy(false);
    }
  }

  async function refreshStatus() {
    setBusy(true);
    setError("");
    setDiagnostic(null);
    console.log("[Global Autopay Refresh] Request started", {
      contractSessionId: session.id,
      isAutopay: session.is_autopay,
      autopayDisabled: session.autopay_disabled,
      verificationId: session.autopay_verification_id,
    });
    try {
      const { data } = await api.post(
        `/contractsessions/${session.id}/autopay/refresh`
      );
      console.log("[Global Autopay Refresh] Request completed", {
        contractSessionId: session.id,
        diagnosticId: data.diagnostic_id,
        verificationStatus: data.verification_status,
        refundStatus: data.refund_status,
        isAutopay: data.is_autopay,
        autopayDisabled: data.autopay_disabled,
        message: data.message,
        diagnostic: data.diagnostic,
      });
      setDiagnostic(data.diagnostic || null);
      setPaymentUrl(data.payment_url || "");
      await refresh();
      if (!data.is_autopay) {
        setError(data.message || "Verification is still pending.");
      }
    } catch (err) {
      const responseDiagnostic = err?.response?.data?.diagnostic || {
        version:
          err?.response?.headers?.["x-global-autopay-refresh-version"] ||
          "not-returned-by-backend",
        diagnosticId:
          err?.response?.data?.diagnostic_id ||
          err?.response?.headers?.["x-global-autopay-diagnostic-id"] ||
          null,
        stage: "not-returned-by-backend",
        contractSessionId: session.id,
        httpStatus: err?.response?.status || null,
        error: err?.response?.data?.error || err?.message,
      };
      console.error("[Global Autopay Refresh] Request failed", {
        contractSessionId: session.id,
        httpStatus: err?.response?.status,
        diagnosticId: err?.response?.data?.diagnostic_id,
        error: err?.response?.data?.error || err?.message,
        diagnostic: responseDiagnostic,
      });
      setDiagnostic(responseDiagnostic);
      setError(
        err?.response?.data?.error || "Unable to refresh autopay status."
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="w-full rounded-lg border border-gray-200 bg-gray-50 p-4 text-xs">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-100 text-mila_orange">
            <CreditCard size={18} />
          </div>
          <div className="flex flex-col gap-1">
            <strong className="text-sm text-gray-800">Automatic card payments</strong>
            <span className="text-gray-500">Session {session.session_start_date} → {session.session_end_date}</span>
          </div>
        </div>
        <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 font-semibold ${
          session.is_autopay ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
        }`}>
          {session.is_autopay && <CheckCircle2 size={12} />}
          {session.autopay_disabled ? "Disabled" : session.is_autopay ? "Enabled" : "Not configured"}
        </span>
      </div>

      {(session.is_autopay || (!session.autopay_disabled && session.autopay_verification_id)) && <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" disabled={busy} onClick={disable}
          className="rounded-md border border-red-300 px-4 py-2 font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">
          Disable autopay
        </button>
        {session.is_autopay && !changingCard && <button type="button" disabled={busy} onClick={() => { setChangingCard(true); setAuthorized(false); setError(""); }}
          className="rounded-md border border-gray-300 px-4 py-2 font-semibold text-gray-700 hover:bg-gray-100 disabled:opacity-50">
          Change card
        </button>}
      </div>}
      {(!session.is_autopay || changingCard) && <div className="mt-4 flex flex-col gap-3 border-t border-gray-200 pt-4">
        <div className="flex items-start gap-2 text-gray-600">
          <ShieldCheck size={16} className="mt-0.5 shrink-0 text-green-600" />
          <span>The card is verified with a $1 charge followed by a $1 refund. Future tuition invoices for this session are charged on their due dates.</span>
        </div>
        {(!paymentUrl || changingCard) && <>
          {changingCard && <span>Autopay will pause when you register the replacement card and resume after verification.</span>}
          <label className="flex cursor-pointer items-center gap-2 text-gray-700">
            <input type="checkbox" checked={authorized} onChange={(event) => setAuthorized(event.target.checked)}
              className="h-4 w-4 accent-orange-500" />
            <span>The student authorized automatic card payments for this session.</span>
          </label>
          <button type="button" disabled={!authorized || busy} onClick={configure}
            className="flex h-10 w-fit min-w-56 items-center justify-center gap-2 rounded-md bg-mila_orange px-5 font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
            {busy ? "Preparing secure link..." : changingCard ? "Register replacement card" : "Register card for autopay"}
          </button>
          {changingCard && <button type="button" disabled={busy} onClick={() => setChangingCard(false)} className="w-fit text-gray-600">Cancel</button>}
        </>}
        {!changingCard && !session.autopay_disabled && paymentUrl.startsWith("https://") && <div className="flex items-center gap-3">
          <a href={paymentUrl} target="_blank" rel="noopener noreferrer"
            className="flex h-10 w-fit items-center justify-center gap-2 rounded-md bg-mila_orange px-5 font-semibold text-white transition hover:bg-orange-600">
            <ExternalLink size={16} /> Open secure card registration
          </a>
          <span className="text-amber-700">Waiting for verification and refund confirmation.</span>
        </div>}
      </div>}
      {(paymentUrl || session.is_autopay) && <button type="button" disabled={busy} onClick={refreshStatus}
        className="mt-3 flex items-center gap-1.5 font-semibold text-gray-600 hover:text-mila_orange disabled:opacity-50">
        <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Refresh status
      </button>}
      {error && <div role="alert" className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-red-700">{error}</div>}
      {error && diagnostic && <details open className="mt-2 rounded border border-gray-300 bg-white px-3 py-2 text-gray-700">
        <summary className="cursor-pointer font-semibold">Technical diagnostics</summary>
        <div className="mt-2 flex justify-end">
          <button type="button" onClick={() => navigator.clipboard?.writeText(JSON.stringify(diagnostic, null, 2))}
            className="rounded border border-gray-300 px-2 py-1 font-semibold hover:bg-gray-50">
            Copy diagnostics
          </button>
        </div>
        <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-gray-100 p-2 text-[11px]">
          {JSON.stringify(diagnostic, null, 2)}
        </pre>
      </details>}
    </div>
  );
}
